import { memo, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { setCurrentQuestionInfo } from '@actions/quizTaking';
import { scrollIntoViewIfNeeded } from '@cont/Quiz/utils';
import useQuestion from '@cont/Quiz/Taking/Objective/Question/useQuestion';
import useQuestionStatus from '@cont/Quiz/RevampedTaking/Navigation/useQuestionStatus';
import { NTA_COLORS } from '../constants';
import NtaPaletteStatusVisual from '../components/NtaPaletteStatusVisual';
import { getNtaPaletteIconFromStatus } from '../ntaPaletteStatus';
import { ntaPageCss } from '../typography';

const Cell = styled.button`
  position: relative;
  width: 38px;
  height: 38px;
  border: none;
  padding: 0;
  cursor: ${({ $locked }) => ($locked ? 'not-allowed' : 'pointer')};
  opacity: ${({ $locked }) => ($locked ? 0.35 : 1)};
  background: transparent;
  outline: ${({ $selected }) =>
    $selected ? `2px solid ${NTA_COLORS.textPrimary}` : 'none'};
  outline-offset: 0;
  ${ntaPageCss}
`;

const NtaPaletteQuestion = ({ rank, sectionUID, questionUID, isLocked }) => {
  const dispatch = useDispatch();
  const blockRef = useRef(null);
  const [isClicked, setIsClicked] = useState(false);
  const { currentQuestion } = useQuestion();

  const {
    isSelected,
    isAttempted,
    isMarked,
    isMarkedAndAttempted,
    isUnanswered
  } = useQuestionStatus(sectionUID, questionUID);

  const paletteIcon = getNtaPaletteIconFromStatus({
    isMarkedAndAttempted,
    isMarked,
    isAttempted,
    isUnanswered
  });

  useEffect(() => {
    if (!isSelected && isClicked) setIsClicked(false);
  }, [isSelected, isClicked]);

  useEffect(() => {
    if (isSelected && !isClicked && blockRef.current) {
      scrollIntoViewIfNeeded({ target: blockRef.current, paddingInPixel: 50 });
    }
  }, [isSelected, isClicked]);

  const handleClick = () => {
    if (isLocked) return;
    setIsClicked(true);
    if (currentQuestion.uid !== questionUID) {
      dispatch(setCurrentQuestionInfo({ sectionUID, questionUID }));
    }
  };

  return (
    <Cell
      ref={blockRef}
      type="button"
      $locked={isLocked}
      $selected={isSelected}
      onClick={handleClick}
      aria-label={`Question ${rank}`}
      aria-current={isSelected ? 'true' : undefined}
    >
      <NtaPaletteStatusVisual icon={paletteIcon} label={rank} size={38} />
    </Cell>
  );
};

export default memo(NtaPaletteQuestion);

NtaPaletteQuestion.propTypes = {
  rank: PropTypes.number.isRequired,
  sectionUID: PropTypes.string.isRequired,
  questionUID: PropTypes.string.isRequired,
  isLocked: PropTypes.bool.isRequired
};
